import {
  forwardRef,
  PropsWithChildren,
  useImperativeHandle,
  useRef,
} from "react"

import { faCheck, faPencil } from "@fortawesome/pro-regular-svg-icons"
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material"
import theme, { FaIcon } from "@skistarab/mui-theme"
import { useTranslation } from "react-i18next"

import SrOnly from "@/components/SrOnly"

type StatusAccordionProps = PropsWithChildren<{
  id: string
  title: string
  subtitle: string
  open: boolean
  onToggle: () => void
  isCompleted?: boolean
  hasError?: boolean
}>

export type StatusAccordionRef = {
  focus: () => void
}

const StatusAccordion = forwardRef<StatusAccordionRef, StatusAccordionProps>(
  (
    { id, title, subtitle, open, onToggle, isCompleted, hasError, children },
    ref
  ) => {
    const { t } = useTranslation()
    const isTablet = useMediaQuery(theme.breakpoints.down("md"))
    const accordionRef = useRef<HTMLDivElement>(null)
    const summaryRef = useRef<HTMLDivElement>(null)

    useImperativeHandle(ref, () => ({
      focus: () => {
        summaryRef.current?.focus({ preventScroll: true })
      },
    }))

    const expandIcon = !open && (
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ fontSize: 16 }}
      >
        {isCompleted && (
          <>
            <FaIcon icon={faCheck} color="success" fontSize="inherit" />
            <SrOnly>{t("statusAccordion.completed")}</SrOnly>
          </>
        )}
        <FaIcon icon={faPencil} color="secondary" fontSize="inherit" />
      </Stack>
    )

    return (
      <Accordion
        ref={accordionRef}
        id={`accordion-${id}`}
        expanded={open}
        elevation={open ? 1 : 0}
        onChange={onToggle}
        disableGutters
        aria-invalid={hasError || undefined}
        sx={{
          borderRadius: 1,
          border: (theme) =>
            `1px solid ${hasError ? theme.palette.error.main : theme.palette.grey[500]}`,
          scrollMarginTop: isTablet ? 100 : 250,
          "&:before": {
            display: "none",
          },
        }}
      >
        <AccordionSummary
          ref={summaryRef}
          sx={{ "& .MuiAccordionSummary-content": { flexDirection: "column" } }}
          id={`${id}-panel-header`}
          aria-controls={`${id}-panel-content`}
          expandIcon={expandIcon}
        >
          <Typography variant="body2" component="h3" fontWeight="bold">
            {title}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        </AccordionSummary>

        <AccordionDetails>{children}</AccordionDetails>
      </Accordion>
    )
  }
)

export default StatusAccordion
